import { removeNewLines } from "../components/LearningOutcomes/utils";

/**
 * Build curriculum map rows for CSV export.
 * @param {Object[]} rows - The curriculum map rows.
 * @param {Object[]} cols - The curriculum map columns.
 * @param {string} mapType - 'current', 'future', or 'both'.
 * @param {Object} options - Additional options.
 *   badgeMappings: badge mapping array
 *   setLoSyncFlag: boolean
 *   futureMap: future map object (with .term)
 *   headers: array of CSV headers
 * @returns {Object[]} Array of row objects for CSV export.
 */
export function buildCurriculumMapRows(rows, cols, mapType, options = {}) {
  const {
    badgeMappings = [],
    setLoSyncFlag = false,
    futureMap = null,
    headers = [],
  } = options;
  const data = [];
  let offering_code = "";
  let subject_code = "";
  const colData = cols.slice(1);

  rows.forEach((row) => {
    if (row.course.code) {
      offering_code = row.course.code;
      subject_code = row.course.course_summary.academic_subject.title;
    }
    colData.forEach((col) => {
      // Helper to add a row
      function addRow({
        map_tense,
        origin,
        destination,
        present_or_upcoming,
        mapping,
        loSync,
        badgeSourceId,
        badgeTargetId,
        badgeLifeCycle,
      }) {
        const { openBadgeId, issuerOpenBadgeId } = getBadgeMapping(
          badgeSourceId,
          badgeTargetId,
          badgeLifeCycle,
          badgeMappings
        );
        const dataRow = {
          map_tense,
          subject_code,
          offering_code,
          origin_context: origin.context,
          origin_id: origin.id,
          origin_name: removeNewLines(origin.name),
          origin_statement: removeNewLines(origin.statement ?? ""),
          destination_context: destination.context,
          destination_id: destination.id,
          destination_name: removeNewLines(destination.name),
          destination_statement: removeNewLines(destination.statement),
          present_or_upcoming,
          degrees_of_learning: mapping?.properties?.degreesOfLearning ?? "",
          mapping_state: mapping ? "mapped" : "unmapped",
        };
        if (headers.includes("set_lo_sync")) {
          dataRow.set_lo_sync =
            !row.course?.outcome_type && setLoSyncFlag ? loSync : "";
        }
        if (
          headers.includes("open_badge_id") &&
          headers.includes("issuer_open_badge_id")
        ) {
          dataRow.open_badge_id = openBadgeId;
          dataRow.issuer_open_badge_id = issuerOpenBadgeId;
        }
        data.push(dataRow);
      }

      // --- CURRENT/BOTH ---
      if (mapType === "current" || mapType === "both") {
        // Current CLOs to current ILO/PLOs
        const mapping = row.course.mappings.find((m) => m.to === col.key);
        const loSync = !!mapping?.properties?.lo_sync;
        addRow({
          map_tense: mapType === "both" ? "both" : "current",
          origin: {
            context: row.course?.outcome_type ? "CSLO" : "offering",
            id: row.course.id,
            name:
              row.course.name ??
              row.course.course_summary.representative_section_name,
            statement: row.course.statement,
          },
          destination: {
            context: col.unformatted.outcome_type,
            id: col.key,
            name: col.unformatted.name,
            statement: col.unformatted.statement,
          },
          present_or_upcoming: "present",
          mapping,
          loSync,
          badgeSourceId: row.course.id,
          badgeTargetId: col.key,
          badgeLifeCycle: mapType === "both" ? "both" : "current",
        });

        // Future CLOs to current ILO/PLOs
        if (row.futureVersions) {
          row.futureVersions.forEach((futureRow) => {
            if (
              !futureMap ||
              !futureMap.term ||
              new Date(futureRow.effective_term.start_at) <
                new Date(futureMap.term.start_at)
            ) {
              const mapping = futureRow.mappings.find((m) => m.to === col.key);
              const loSync = !!mapping?.properties?.lo_sync;
              addRow({
                map_tense: mapType === "both" ? "both" : "current",
                origin: {
                  context: "CSLO",
                  id: futureRow.id,
                  name: futureRow.name,
                  statement: futureRow.statement,
                },
                destination: {
                  context: col.unformatted.outcome_type,
                  id: col.key,
                  name: col.unformatted.name,
                  statement: col.unformatted.statement,
                },
                present_or_upcoming: "upcoming",
                mapping,
                loSync,
                badgeSourceId: futureRow.id,
                badgeTargetId: col.key,
                badgeLifeCycle: "both",
              });
            }
          });
        }

        // Current CLOs to future ILO/PLOs
        col.futureVersions.forEach((futureCol) => {
          if (
            !futureMap ||
            !futureMap.term ||
            new Date(futureCol.effective_term.start_at) <
              new Date(futureMap.term.start_at)
          ) {
            const mapping = row.course.mappings.find(
              (m) => m.to === futureCol.key
            );
            const loSync = !!mapping?.properties?.lo_sync;
            addRow({
              map_tense: mapType === "both" ? "both" : "current",
              origin: {
                context: row.course?.outcome_type ? "CSLO" : "offering",
                id: row.course.id,
                name:
                  row.course.name ??
                  row.course.course_summary.representative_section_name,
                statement: row.course.statement,
              },
              destination: {
                context: futureCol.outcome_type,
                id: futureCol.id,
                name: futureCol.name,
                statement: futureCol.statement,
              },
              present_or_upcoming: "upcoming",
              mapping,
              loSync,
              badgeSourceId: row.course.id,
              badgeTargetId: futureCol.key,
              badgeLifeCycle: "both",
            });
          }
        });

        // Future CLOs to future ILO/PLOs
        if (row.futureVersions) {
          row.futureVersions.forEach((futureRow) => {
            col.futureVersions.forEach((futureCol) => {
              if (
                !futureMap ||
                !futureMap.term ||
                (new Date(futureCol.effective_term.start_at) <
                  new Date(futureMap.term.start_at) &&
                  new Date(futureRow.effective_term.start_at) <
                    new Date(futureMap.term.start_at))
              ) {
                const mapping = futureRow.mappings.find(
                  (m) => m.to === futureCol.key
                );
                const loSync = !!mapping?.properties?.lo_sync;
                addRow({
                  map_tense: mapType === "both" ? "both" : "current",
                  origin: {
                    context: "CSLO",
                    id: futureRow.id,
                    name: futureRow.name,
                    statement: futureRow.statement,
                  },
                  destination: {
                    context: futureCol.outcome_type,
                    id: futureCol.id,
                    name: futureCol.name,
                    statement: futureCol.statement,
                  },
                  present_or_upcoming: "upcoming",
                  mapping,
                  loSync,
                  badgeSourceId: futureRow.id,
                  badgeTargetId: futureCol.key,
                  badgeLifeCycle: "both",
                });
              }
            });
          });
        }
      }

      // --- FUTURE ---
      if (mapType === "future") {
        // Current CLOs to current ILO/PLOs
        const mapping = row.course.mappings.find((m) => m.to === col.key);
        const loSync = !!mapping?.properties?.lo_sync;
        addRow({
          map_tense: "future",
          origin: {
            context: row.course?.outcome_type ? "CSLO" : "offering",
            id: row.course.id,
            name:
              row.course.name ??
              row.course.course_summary.representative_section_name,
            statement: row.course.statement,
          },
          destination: {
            context: col.unformatted.outcome_type,
            id: col.key,
            name: col.unformatted.name,
            statement: col.unformatted.statement,
          },
          present_or_upcoming: "present",
          mapping,
          loSync,
          badgeSourceId: row.course.id,
          badgeTargetId: col.key,
          badgeLifeCycle: "future",
        });

        // Future CLOs to current ILO/PLOs
        if (row.futureVersions) {
          row.futureVersions.forEach((futureRow) => {
            if (
              new Date(futureRow.effective_term.start_at) >
              new Date(futureMap.term.start_at)
            ) {
              const mapping = futureRow.mappings.find((m) => m.to === col.key);
              const loSync = !!mapping?.properties?.lo_sync;
              addRow({
                map_tense: "future",
                origin: {
                  context: "CSLO",
                  id: futureRow.id,
                  name: futureRow.name,
                  statement: futureRow.statement,
                },
                destination: {
                  context: col.unformatted.outcome_type,
                  id: col.key,
                  name: col.unformatted.name,
                  statement: col.unformatted.statement,
                },
                present_or_upcoming: "upcoming",
                mapping,
                loSync,
                badgeSourceId: futureRow.id,
                badgeTargetId: col.key,
                badgeLifeCycle: "future",
              });
            }
          });
        }

        // Current CLOs to future ILO/PLOs
        col.futureVersions.forEach((futureCol) => {
          if (
            new Date(futureCol.effective_term.start_at) >
            new Date(futureMap.term.start_at)
          ) {
            const mapping = row.course.mappings.find(
              (m) => m.to === futureCol.key
            );
            const loSync = !!mapping?.properties?.lo_sync;
            addRow({
              map_tense: "future",
              origin: {
                context: row.course?.outcome_type ? "CSLO" : "offering",
                id: row.course.id,
                name:
                  row.course.name ??
                  row.course.course_summary.representative_section_name,
                statement: row.course.statement,
              },
              destination: {
                context: futureCol.outcome_type,
                id: futureCol.id,
                name: futureCol.name,
                statement: futureCol.statement,
              },
              present_or_upcoming: "upcoming",
              mapping,
              loSync,
              badgeSourceId: row.course.id,
              badgeTargetId: futureCol.key,
              badgeLifeCycle: "future",
            });
          }
        });

        // Future CLOs to future ILO/PLOs
        if (row.futureVersions) {
          row.futureVersions.forEach((futureRow) => {
            col.futureVersions.forEach((futureCol) => {
              if (
                new Date(futureCol.effective_term.start_at) >
                  new Date(futureMap.term.start_at) &&
                new Date(futureRow.effective_term.start_at) >
                  new Date(futureMap.term.start_at)
              ) {
                const mapping = futureRow.mappings.find(
                  (m) => m.to === futureCol.key
                );
                const loSync = !!mapping?.properties?.lo_sync;
                addRow({
                  map_tense: "future",
                  origin: {
                    context: "CSLO",
                    id: futureRow.id,
                    name: futureRow.name,
                    statement: futureRow.statement,
                  },
                  destination: {
                    context: futureCol.outcome_type,
                    id: futureCol.id,
                    name: futureCol.name,
                    statement: futureCol.statement,
                  },
                  present_or_upcoming: "upcoming",
                  mapping,
                  loSync,
                  badgeSourceId: futureRow.id,
                  badgeTargetId: futureCol.key,
                  badgeLifeCycle: "future",
                });
              }
            });
          });
        }
      }
    });
  });
  return data;
}

export const getBadgeMapping = (
  sourceOutcomeId,
  targetOutcomeId,
  lifeCycle,
  badgeMappings
) => {
  if (!badgeMappings || badgeMappings.length === 0) {
    return { openBadgeId: "", issuerOpenBadgeId: "" };
  }

  const mapping = badgeMappings.find((m) => {
    if (lifeCycle === "both") {
      return (
        m.sourceOutcomeId === sourceOutcomeId &&
        m.targetOutcomeId === targetOutcomeId &&
        (m.lifeCycle === "current" || m.lifeCycle === "future")
      );
    }
    return (
      m.sourceOutcomeId === sourceOutcomeId &&
      m.targetOutcomeId === targetOutcomeId &&
      m.lifeCycle === lifeCycle
    );
  });

  if (mapping) {
    console.log("Badge mapping found:", mapping);
    return {
      openBadgeId: mapping.openBadgeId,
      issuerOpenBadgeId: mapping.issuerOpenBadgeId,
    };
  }

  return { openBadgeId: "", issuerOpenBadgeId: "" };
};
